import { useEffect, useRef, useState } from 'react'
import type { MouseEvent, FC } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { rqKeys } from '@/react-query-keys'
import { formatMoney, getMs } from '@/helpers'
import { apiRequest, apiRequestNew } from '@/helpers/request'
import {
  readPendingPurchaseQueue,
  removePendingPurchaseQueueItem,
  upsertPendingPurchaseQueueItem,
} from '@/helpers/certificate-purchase-pending-queue'
import { processPayment } from '@/helpers/payment'
import { useOnlineStatus } from '@/helpers/hooks'
import { useAppStore } from '@/store'
import type { E_CertificateTypes } from '@/enums'
import { Button, Drawer } from '@/components'
import { OrganizationBanner } from '@/b-components/organization-banner'
import type { T_UniRes } from '@/types'
import { E_CodeTypes, type T_CertificatesListRes, type T_CertificatesRestListRes } from '@/types/responses'
import { useOrganizationsDrawerStore } from '../organizations-drawer/store'
import { CertificateInstruction } from '../../instructions'
import step1Svg from './img/step1.svg'
import step2Svg from './img/step2.svg'
import step3Svg from './img/step3.svg'

import {
  SC_DenominationItem,
  SC_DenominationsList,
  SC_Step,
  SC_StepsWrapper,
  SC_VertLine,
  SC_ButtonsWrapper,
  SC_DrawerContent,
} from './styled'

interface T_BuyCertificateDrawerProps {
  hideBuyCertificateDrawer: () => void
  showOrganizationsDrawer: () => void
}

export const BuyCertificateDrawer: FC<T_BuyCertificateDrawerProps> = ({
  hideBuyCertificateDrawer,
  showOrganizationsDrawer,
}) => {
  const queryClient = useQueryClient()
  const cacheResetRef = useRef<boolean>(false)
  const pingTimersRef = useRef<number[]>([])
  const keepPingAliveOnUnmountRef = useRef<boolean>(false)
  const userData = useAppStore((s) => s.userData)
  const isOnline = useOnlineStatus()

  useEffect(() => {
    return () => {
      // If drawer is closed without a successful purchase, stop pending pings.
      if (keepPingAliveOnUnmountRef.current) return
      for (const t of pingTimersRef.current) window.clearTimeout(t)
      pingTimersRef.current = []
    }
  }, [])

  const certificateNominalsList = useAppStore((s) => s.certificateNominalsList)
  const certificateCategoriesList = useAppStore((s) => s.certificateCategoriesList)

  const getCertData = () => {
    if (!certificateNominalsForSelectedOrganization) return null

    const categoryForSelectedOrganization = certificateNominalsForSelectedOrganization[0].category

    const found = certificateCategoriesList.find((c) => (
      c.id === categoryForSelectedOrganization)
    )

    if (!found) return null

    return found
  }

  const sO = useOrganizationsDrawerStore((s) => s.selectedOrganization)

  const certificateNominalsForSelectedOrganization = certificateNominalsList.find((cN) => (
    cN.categoryId === sO?.id
  ))?.list.filter((cN) => cN.category === sO?.id) // TODO: Remove after fix API response

  const [ instructionsShown, setInstructionsShown ] = useState(false)

  // Certificates rest
  const { data: certificatesRest } = useQuery<T_CertificatesRestListRes>({
    queryKey: [ rqKeys.certificatesRest ],
    queryFn: () => (
      apiRequestNew(
        'POST',
        '/delivery/goods/rest',
        [
          [
            1, [
              ...(certificateNominalsForSelectedOrganization || []).map((c) => c.id)
            ],
          ],
        ],
      )
    ),
  })

  const [ selectedNominalItemId, setSelectedNominalItemId ] = useState<number>()

  const selectNominalItem = (e: MouseEvent<HTMLElement>) => {
    setSelectedNominalItemId(
      Number(e.currentTarget.dataset.nominalId) || undefined
    )
  }

  const [ isPaymentInProcess, setIsPaymentInProcess ] = useState<boolean>(false)

  const optimisticAddPurchasedCertificate = (nominalId: number, documentId?: number) => {
    const now = Date.now()
    const rand = Math.floor(Math.random() * 1000)
    const tempId = -((now * 1000) + rand)

    upsertPendingPurchaseQueueItem(userData.id, { tempId, nominalId, createdAt: now, documentId })

    queryClient.setQueryData<T_CertificatesListRes>(
      [ rqKeys.certificatesList ],
      (old) => {
        const next = [
          {
            id: tempId,
            code: '',
            goods: nominalId,
            time: now,
            type: E_CodeTypes.QRCode,
          },
          ...(old || []),
        ]
          // Deduplicate by id just in case
          .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)

        return next
      }
    )

    return { tempId, nominalId, createdAt: now, documentId }
  }

  const refetchAfterPurchase = (optimistic: { tempId: number, nominalId: number, createdAt: number, documentId?: number }) => {
    // Stop previous ping session if any
    for (const t of pingTimersRef.current) window.clearTimeout(t)
    pingTimersRef.current = []

    // Ping real certificate after purchase
    const delays = [ getMs('3s'), getMs('5s'), getMs('15s'), getMs('30s'), getMs('60s') ]

    const normalizeTimeMs = (t: unknown) => {
      const n = Number(t)
      if (!Number.isFinite(n) || n <= 0) return 0
      // Backend may return seconds; Date.now() is ms.
      return n < 1e12 ? n * 1000 : n
    }

    const isRealPurchasedCertificate = (list: T_CertificatesListRes) => {
      const candidates = (list || []).filter((c) => (
        Number(c?.id) > 0
        && Number(c?.goods) === Number(optimistic.nominalId)
        && typeof c?.code === 'string'
        && c.code.trim() !== ''
      ))
      if (!candidates.length) return false

      const purchaseDocumentId = Number(optimistic.documentId)
      if (Number.isFinite(purchaseDocumentId) && purchaseDocumentId > 0) {
        const byDocumentId = candidates.some((c) => Number(c.document) === purchaseDocumentId)
        if (byDocumentId) return true

        const allCandidatesWithoutDocument = candidates.every((c) => !(Number(c.document) > 0))
        const waitedLongEnough = (Date.now() - optimistic.createdAt) >= getMs('3m')
        if (allCandidatesWithoutDocument && waitedLongEnough) {
          return candidates.some((c) => {
            const t = normalizeTimeMs(c.time)
            if (!t) return false
            return Math.abs(t - optimistic.createdAt) <= getMs('10m')
          })
        }
        return false
      }

      return candidates.some((c) => {
        const t = normalizeTimeMs(c.time)
        if (!t) return false
        return Math.abs(t - optimistic.createdAt) <= getMs('10m')
      })
    }

    const mergeServerWithOptimistic = (serverList: T_CertificatesListRes, opts: { confirmed: boolean }) => {
      const resolvedForQueue: number[] = []
      queryClient.setQueryData<T_CertificatesListRes>(
        [ rqKeys.certificatesList ],
        (old) => {
          const prev = old || []
          const purchaseQueue = readPendingPurchaseQueue(userData.id)
          const purchasePendingTempIds = new Set(purchaseQueue.map((p) => p.tempId))
          const purchaseQueueByTempId = new Map(purchaseQueue.map((p) => [ p.tempId, p ]))
          const pending = prev.filter((c) => c.id < 0 && purchasePendingTempIds.has(c.id))
          const otherOptimistic = prev.filter((c) => c.id < 0 && !purchasePendingTempIds.has(c.id))

          const resolvedPendingIds = new Set<number>()

          for (const p of pending) {
            // Keep our own placeholder until backend confirms real cert
            if (!opts.confirmed && p.id === optimistic.tempId) continue
            const pTimeMs = normalizeTimeMs(p.time)
            const queuedMeta = purchaseQueueByTempId.get(p.id)
            const pendingDocumentId = Number(queuedMeta?.documentId)
            const candidates = serverList.filter((s) => (
              Number(s.id) > 0
              && Number(s.goods) === Number(p.goods)
              && typeof s.code === 'string'
              && s.code.trim() !== ''
            ))
            const hasMatch = (() => {
              if (Number.isFinite(pendingDocumentId) && pendingDocumentId > 0) {
                const byDocumentId = candidates.some((s) => Number(s.document) === pendingDocumentId)
                if (byDocumentId) return true

                const allCandidatesWithoutDocument = candidates.every((s) => !(Number(s.document) > 0))
                const waitedLongEnough = pTimeMs ? ((Date.now() - pTimeMs) >= getMs('3m')) : false
                if (allCandidatesWithoutDocument && waitedLongEnough) {
                  return candidates.some((s) => {
                    const t = normalizeTimeMs(s.time)
                    if (!t || !pTimeMs) return false
                    return Math.abs(t - pTimeMs) <= getMs('10m')
                  })
                }
                return false
              }

              return candidates.some((s) => {
                const t = normalizeTimeMs(s.time)
                if (!t || !pTimeMs) return false
                return Math.abs(t - pTimeMs) <= getMs('10m')
              })
            })()

            const isTooOld = pTimeMs ? ((Date.now() - pTimeMs) > getMs('60m')) : false
            if (hasMatch || isTooOld) resolvedPendingIds.add(p.id)
          }

          const keepPending = pending.filter((p) => !resolvedPendingIds.has(p.id))
          resolvedForQueue.push(...Array.from(resolvedPendingIds))

          const next = [ ...otherOptimistic, ...keepPending, ...serverList ]
            .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)

          const hasOptimistic = next.some((c) => c.id === optimistic.tempId)

          if (!opts.confirmed && !hasOptimistic) {
            next.unshift({
              id: optimistic.tempId,
              code: '',
              goods: optimistic.nominalId,
              time: optimistic.createdAt,
              type: E_CodeTypes.QRCode,
            })
          }

          if (opts.confirmed) {
            if (!resolvedForQueue.includes(optimistic.tempId)) {
              resolvedForQueue.push(optimistic.tempId)
            }
            return next.filter((c) => c.id !== optimistic.tempId)
          }

          return next
        }
      )

      for (const tempId of resolvedForQueue) {
        removePendingPurchaseQueueItem(userData.id, tempId)
      }
    }

    let resolved = false
    let forced = false

    const forceUpdateOnce = () => {
      if (forced) return
      forced = true
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return
      void Promise.allSettled([
        queryClient.refetchQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ], exact: true }),
        queryClient.refetchQueries({ queryKey: [ rqKeys.userBalance ], exact: true }),
        queryClient.refetchQueries({ queryKey: [ rqKeys.transactionsKey ], exact: true }),
      ])
    }

    const clearTimers = () => {
      for (const t of pingTimersRef.current) window.clearTimeout(t)
      pingTimersRef.current = []
    }

    for (const delay of delays) {
      const tid = window.setTimeout(async () => {
        if (resolved) return
        if (typeof navigator !== 'undefined' && navigator.onLine === false) return

        try {
          cacheResetRef.current = true
          const serverActive = await apiRequestNew<T_CertificatesListRes>(
            'GET',
            '/era/gift/certificate?offset=0&limit=9999',
            undefined,
            cacheResetRef,
          )

          const list = Array.isArray(serverActive) ? serverActive : []
          const confirmed = isRealPurchasedCertificate(list)

          mergeServerWithOptimistic(list, { confirmed })

          if (confirmed) {
            resolved = true
            clearTimers()
            forceUpdateOnce()
          }
        } catch {
          // ignore - next tick will retry
        } finally {
          cacheResetRef.current = false
        }
      }, delay)

      pingTimersRef.current.push(tid)
    }
  }

  const buyCertificate = async () => {
    if (!isOnline) {
      toast.error('Нет подключения к интернету. Подключитесь, пожалуйста, к сети и попробуйте снова.')
      return
    }

    const nominalId = selectedNominalItemId
    if (!nominalId) return

    const selectedNominal = (certificateNominalsForSelectedOrganization || []).find(
      (item) => item.id === nominalId
    )
    const certificatePriceCents = Number(selectedNominal?.price?.base?.rur || 0)
    const userBalanceCents = Math.round((Number(userData.mainAccount) || 0) * 100)

    if (userBalanceCents < certificatePriceCents) {
      toast.error('Недостаточно средств на счёте для покупки. Пополните, пожалуйста, счет и попробуйте снова')
      return
    }

    setIsPaymentInProcess(true)

    try {
      // Создаем платёжный документ на выбранный сертификат
      const orderCreateRes = await apiRequest<T_UniRes<{ id: number }>>({
        method: 'delivery/order/create',
        param: {
          cart: [ { id: nominalId, count: 1 } ],
        },
      })

      if (orderCreateRes.code === 'data-wrong') {
        toast('Ошибка оплаты. Попробуйте ещё раз позднее.', { type: 'error' })
        setSelectedNominalItemId(undefined)
        return
      }

      const documentId = orderCreateRes?.data?.id

      if (!documentId) {
        toast('Ошибка оплаты. Попробуйте ещё раз позднее.', { type: 'error' })
        return
      }

      const success = await processPayment(
        documentId,
        toast,
        (errorMessage) => toast(errorMessage, { type: 'error' })
      )

      if (success) {
        const optimistic = optimisticAddPurchasedCertificate(nominalId, documentId)
        refetchAfterPurchase(optimistic)
        // Allow ping timers to continue even after drawer closes (component unmount).
        keepPingAliveOnUnmountRef.current = true
        hideBuyCertificateDrawer()
      }
    } catch {
      toast('Ошибка оплаты. Попробуйте ещё раз позднее.', { type: 'error' })
    } finally {
      setIsPaymentInProcess(false)
    }
  }


  if (!certificateNominalsForSelectedOrganization) return null


  return (
    <Drawer
      backTitle={
        instructionsShown
          ? 'Инструкция по применению'
          : 'Продуктовый сертификат'
      }
      onClose={hideBuyCertificateDrawer}
      isBackdropClosable
      onBackClick={() => {
        if (instructionsShown) {
          setInstructionsShown(false)
        } else {
          hideBuyCertificateDrawer()
          showOrganizationsDrawer()
        }
      }}
    >
      <SC_DrawerContent>
        {instructionsShown ? (
          <CertificateInstruction
            certificateType={getCertData()?.name as E_CertificateTypes}
            categoryId={getCertData()?.id}
            actionLabel='Приобрести'
            onActionClick={() => setInstructionsShown(false)}
          />
        ) : <>
          <div style={{ marginBottom: '10px' }}>
            <OrganizationBanner
              isShowId
              certificateId={getCertData()?.id as number}
              certificateType={getCertData()?.name as E_CertificateTypes}
              buyCertificate
            />
          </div>

          <h3 style={{ fontWeight: 500, fontSize: '17px', lineHeight: '110%' }}>
            Выберите номинал
          </h3>

          {certificatesRest?.[0].some((restNumber) => restNumber > 0) ? (
            <SC_DenominationsList>
              {(certificateNominalsForSelectedOrganization || []).map((c, i) => (
                (certificatesRest || [])[0][i] > 0 && (
                  <SC_DenominationItem
                    key={c.id}
                    onClick={selectNominalItem}
                    data-nominal-id={c.id}
                    $isSelected={selectedNominalItemId === c.id}
                  >
                    {formatMoney(c.price?.base?.rur, true)}
                  </SC_DenominationItem>
                )
              ))}
            </SC_DenominationsList>
          ) : (
            <div style={{ padding: '10px 0 20px' }}>
              Нет доступных вариантов
            </div>
          )}

          <h3 style={{ fontWeight: 500, fontSize: '17px', lineHeight: '110%' }}>
            Как это работает
          </h3>

          <SC_StepsWrapper>
            <SC_Step>
              <div><img src={step1Svg}/></div>
              <span>Оформите сертификат на любую удобную вам сумму</span>
            </SC_Step>

            <SC_VertLine />

            <SC_Step>
              <div><img src={step2Svg}/></div>
              <span>Откройте сертификат и покажите QR-код кассиру</span>
            </SC_Step>

            <SC_VertLine />

            <SC_Step>
              <div><img src={step3Svg} /></div>
              <span>Кассир отсканирует код, и оплата пройдет автоматически</span>
            </SC_Step>
          </SC_StepsWrapper>

          <p style={{ marginBottom: '24px' }}>
            Остаток по сертификату не отображается в приложении<br />
            Узнать его можно на кассе — просто попросите кассира проверить баланс
          </p>

          <SC_ButtonsWrapper>
            <Button
              type='primary'
              block
              disabled={!selectedNominalItemId}
              onClick={buyCertificate}
              loading={isPaymentInProcess}
            >
              Приобрести
            </Button>

            <Button
              block
              type='default'
              onClick={() => setInstructionsShown(true)}
            >
              Подробная инструкция
            </Button>
          </SC_ButtonsWrapper>
        </>}
      </SC_DrawerContent>
    </Drawer>
  )
}
